import React, {
  ReactElement,
  ReactNode,
  isValidElement,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  Ref,
} from 'react'
import {
  ActivityIndicator,
  ColorValue,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  NativeTouchEvent,
  Platform,
  Pressable,
  PressableProps,
  ScrollView,
  StyleProp,
  StyleSheet,
  TargetedEvent,
  TextStyle,
  View,
  ViewProps,
  ViewStyle,
  useWindowDimensions,
} from 'react-native'
import { Href } from 'zens-router'

import { Color, Radius, Spacing } from '../../style'
import { Chip } from '../chip'
import { Icon } from '../icon'
import { TextInput } from '../textinput'
import Text, { TextStyles } from '../typography/text'
import { OutsidePressHandler } from '../../helper'

import SelectOption from './option'

declare module 'react' {
  function forwardRef<T, P = object>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null
}

interface StyleConfig {
  select?: ViewStyle
  hovered?: ViewStyle
  active?: ViewStyle
  options?: ViewStyle
  text?: TextStyle
  placeholder?: TextStyle
}

type Config = {
  default?: StyleConfig
  round?: StyleConfig
}

const styleConfig: Config = {
  default: StyleSheet.create({
    select: {
      borderRadius: Radius.S,
    },
    hovered: {
      borderRadius: Radius.S + 1,
    },
    active: {
      borderRadius: Radius.S + 2,
    },
    options: {
      borderRadius: Radius.S,
    },
  }),
  /*eslint-disable react-native/no-unused-styles */
  round: StyleSheet.create({
    select: {
      borderRadius: Radius.L,
    },
    hovered: {
      borderRadius: Radius.L + 1,
    },
    active: {
      borderRadius: Radius.L + 2,
    },
    options: {
      borderRadius: Radius.L,
    },
  }),
  /*eslint-enable react-native/no-unused-styles */
}

const styles = StyleSheet.create({
  container: {
    minHeight: 40,
  },
  button: {
    minHeight: 40,
  },
  select: {
    flex: 1,
    minHeight: 40,
    borderWidth: 1,
    borderColor: Color.calmGrey,
    backgroundColor: Color.simplyWhite,
  },
  filled: {
    borderColor: Color.premiumBlack,
  },
  disabled: {
    backgroundColor: Color.sleepingGrey,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: Spacing.S,
  },
  value: {
    flex: 1,
    marginVertical: Spacing.XXS,
  },
  text: {
    flex: 1,
    color: Color.premiumBlack,
    ...TextStyles[6],
  },
  placeholder: {
    color: Color.calmGrey,
  },
  hovered: {
    borderWidth: 1,
    margin: -1,
    minHeight: 42,
  },
  active: {
    borderColor: Color.greyBlue,
    borderWidth: 2,
    margin: -2,
    minHeight: 44,
    minWidth: 44,
  },
  unfolded: {
    borderBottomStartRadius: 0,
    borderBottomEndRadius: 0,
  },
  outline: {
    borderColor: Color.calmGrey,
  },
  outlineError: {
    borderColor: Color.soothingPink,
  },
  icon: {
    marginLeft: Spacing.S,
  },
  iconDisabled: {
    color: Color.calmGrey,
  },
  wrapper: {
    overflow: 'hidden',
  },
  options: {
    zIndex: 9999,
    position: 'absolute',
    top: 0,
    minWidth: '100%',
    backgroundColor: Color.simplyWhite,
    borderWidth: 1,
    borderColor: Color.calmGrey,
    borderTopStartRadius: 0,
    borderTopEndRadius: 0,
    borderTopWidth: 0,
    overflow: 'hidden',
  },
  option: {
    paddingRight: 2 * Spacing.S + 16,
  },
  section: {
    borderTopWidth: 1,
    borderColor: Color.calmGrey,
    backgroundColor: Color.simplyWhite,
  },
  sectionTitle: {
    color: Color.metalGrey,
    fontSize: 14,
  },
  search: {
    zIndex: 10,
  },
  chip: {
    marginRight: Spacing.XS,
    marginVertical: Spacing.XXS,
  },
  empty: {
    minHeight: 40,
    paddingHorizontal: Spacing.S,
    lineHeight: 40,
  },
  skeletonContainer: {
    paddingHorizontal: Spacing.S,
    justifyContent: 'center',
  },
  checkbox: {
    backgroundColor: Color.calmGrey,
  },
  fetching: {
    marginVertical: Spacing.XS,
  },
})

export interface Section<T = string> {
  title?: string
  items: (Option<T> | ReactElement)[]
  style?: ViewStyle
  href?: Href
}

export interface Option<T = string> {
  value: T
  title?: string
  render?: (option?: Option<T>) => ReactElement
  disabled?: boolean
  style?: StyleProp<ViewStyle>
  hoveredStyle?: StyleProp<ViewStyle>
  href?: Href
}

export type Item<T = string> = Option<T> | Section<T> | ReactElement

export interface Props<T> extends Omit<PressableProps, 'style'> {
  placeholder?: string
  items?: Item<T>[]
  footer?: ReactElement
  error?: boolean
  round?: boolean
  style?: StyleProp<ViewStyle>
  contentContainerStyle?: StyleProp<ViewStyle>
  contentOutlineStyle?: StyleProp<ViewStyle>
  valueStyle?: StyleProp<ViewStyle>
  arrowStyle?: StyleProp<TextStyle>
  optionStyle?: StyleProp<ViewStyle>
  optionTextStyle?: StyleProp<TextStyle>
  sectionStyle?: StyleProp<ViewStyle>
  placeholderTextColor?: ColorValue
  filterSelected?: boolean
  value?: T | T[]
  renderValue?: (value?: T | T[]) => ReactElement
  onSelect?: (value: T | T[]) => void
  onSearch?: (value: string) => void
  searchable?: boolean
  isSearching?: boolean
  isFetching?: boolean
  onEndReached?: () => void
  multiple?: boolean
  emptyPlaceholder?: string | ReactElement
  unfoldOnPressOutside?: boolean
  test_ref?: Ref<any>
}

const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }: NativeScrollEvent) => {
  const paddingToBottom = 20
  return layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom
}

function BaseSelect<T>(props: Props<T>) {
  const {
    placeholder,
    value,
    items,
    footer,
    error,
    round,
    style,
    contentContainerStyle,
    contentOutlineStyle,
    valueStyle,
    renderValue,
    arrowStyle,
    optionStyle,
    optionTextStyle,
    sectionStyle,
    placeholderTextColor,
    filterSelected,
    multiple,
    emptyPlaceholder,
    onSelect,
    onSearch,
    searchable,
    isSearching,
    isFetching,
    onEndReached,
    disabled,
    onFocus,
    onBlur,
    onPress,
    testID,
    unfoldOnPressOutside = true,
    test_ref,
    ...otherProps
  } = props
  const findOption = (value: any) => {
    if (!items) {
      return undefined
    }
    if (Array.isArray(value)) {
      let found: Option<T>[] = []
      items.forEach((item) => {
        const sectionOptions = (item as Section<T>)?.items as Option<T>[]
        if (sectionOptions) {
          found = [
            ...found,
            ...sectionOptions.filter((opt) => !!value.find((item) => item === (opt as Option<T>).value)),
          ]
        } else {
          const opt = item as Option<T>
          if (value.find((item) => item === opt.value) !== undefined) {
            found.push(opt)
          }
        }
      })
      return found
    } else {
      let found: Option<T> | undefined
      ;(items as (Option<T> | Section<T> | ReactElement)[]).every((item) => {
        const sectionOptions = (item as Section<T>)?.items as Option<T>[]
        if (sectionOptions) {
          found = sectionOptions.find((opt) => opt.value === value)
        } else {
          const opt = item as Option<T>
          if (opt.value === value) found = opt
        }
        return !found
      })
      return found
    }
  }

  const getSearchValue = () => {
    return (!multiple && (selected as Option<T>)?.value !== undefined && (selected as Option<T>)?.title) || ''
  }

  const selected = findOption(value)
  const [active, setActive] = useState(false)
  const [width, setWidth] = useState<number>()
  const [optionsHeight, setOptionsHeight] = useState<number>()
  const [activeOption, setActiveOption] = useState<string>()
  const [search, setSearch] = useState('')
  const [unfolded, setUnfolded] = useState(false)
  const button = useRef<any>(null)
  const isUnfolded = useRef(false)
  const hasActiveOption = useRef(false)
  const windowDimensions = useWindowDimensions()
  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }

  useEffect(() => {
    isUnfolded.current = unfolded
  }, [unfolded])

  useEffect(() => {
    hasActiveOption.current = !!activeOption
  }, [activeOption])

  useEffect(() => {
    if (disabled) setActive(false)
  }, [disabled])

  const updateOptionActive = (value: boolean, option: Option<any>) => () => {
    if (!value && activeOption === option?.value) {
      setActiveOption(undefined)
    } else {
      setActiveOption(option?.value)
    }
  }

  useImperativeHandle(test_ref, () => ({
    layout: (event: LayoutChangeEvent) => {
      layout(event)
    },
  }))

  const toggleUnfolded = (event: NativeSyntheticEvent<NativeTouchEvent>) => {
    updateUnfolded(!unfolded)
    onPress && onPress(event)
  }

  const updateUnfolded = (value: boolean) => {
    const pageY = button.current?.getBoundingClientRect?.()?.y || 0
    const maxHeight = Math.max(windowDimensions.height - pageY - 40 - Spacing.M, 140)
    setOptionsHeight(maxHeight)
    setUnfolded(value)
    setActiveOption(undefined)
  }

  const focus = (event: NativeSyntheticEvent<TargetedEvent>) => {
    updateActive(true)(event)
    updateUnfolded(true)
  }

  const blur = (event: NativeSyntheticEvent<TargetedEvent>) => {
    setTimeout(() => {
      updateActive(false)(event)
      if (isUnfolded.current && !hasActiveOption.current) {
        updateUnfolded(false)
      }
    }, 300)
    setSearch('')
  }

  const select = (option: Option<T>) => () => {
    if (multiple) {
      const newSelected = ((selected as Option<T>[]) || [])?.filter((item) => item.value !== option?.value)
      if (!(selected as Option<T>[])?.length || (selected as Option<T>[])?.length === newSelected.length) {
        newSelected.push(option)
      }
      onSelect?.(newSelected.map((item) => item.value))
    } else {
      onSelect?.(option?.value)
      onSearch?.('')
    }
    if (!multiple || !hasActiveOption) {
      updateUnfolded(false)
    }
  }
  function sectionHasAllSelected(section: Section<T>) {
    const sectionOptions = section.items as Option<T>[]
    return !!sectionOptions?.every((item) => !!(selected as Option<T>[])?.find((opt) => opt === item))
  }
  function sectionHasSomeSelected(section: Section<T>) {
    const sectionOptions = section.items as Option<T>[]
    return !!sectionOptions?.find((item) => !!(selected as Option<T>[])?.find((opt) => opt === item))
  }

  const selectSection = (section: Section<T>) => () => {
    const sectionOptions = section.items as Option<T>[]
    const allSelected = sectionHasAllSelected(section)
    const newSelected = [
      ...[...((selected as Option<T>[]) || [])].filter((item) => !sectionOptions.find((opt) => opt === item)),
      ...(allSelected ? [] : sectionOptions),
    ]
    onSelect?.(newSelected.map((item) => item.value))
  }

  const layout = (event: LayoutChangeEvent) => {
    setWidth(Math.round(event.nativeEvent.layout.width))
  }

  const variantConfig = round ? styleConfig.round : styleConfig.default

  const renderSelected = () => {
    if (!selected) return undefined

    if (multiple) {
      const options = selected as Option<T>[]
      return options?.map((option) => (
        <Chip
          key={`${option?.value}`}
          title={`${option?.title || option?.value}`}
          closeable
          style={styles.chip}
          onClose={select(option)}
        />
      ))
    } else {
      const option = selected as Option<T>
      return option?.render ? option?.render(option) : option?.title || option?.value
    }
  }

  const renderContent = (state: { pressed: boolean; hovered?: boolean }) => {
    const { hovered } = state

    const selectedValue = renderSelected()
    const hasValue = Array.isArray(selectedValue) ? selectedValue.length > 0 : !!selectedValue
    const outlineStyle = [
      styles.outline,
      hasValue && !disabled && styles.filled,
      disabled && styles.disabled,
      !!error && styles.outlineError,
      active && !unfolded && styles.active,
      active && !unfolded && variantConfig?.active,
      !active && hovered && styles.hovered,
      !active && hovered && variantConfig?.hovered,
      contentOutlineStyle,
    ]

    const contentStyle = [
      styles.select,
      variantConfig?.select,
      hasValue && !disabled && styles.filled,
      disabled && styles.disabled,
      !!error && styles.outlineError,
      unfolded && styles.unfolded,
      styles.content,
      { minWidth: width },
      valueStyle,
    ]
    const textStyle = [
      styles.value,
      styles.text,
      variantConfig?.text,
      !hasValue && styles.placeholder,
      !hasValue && variantConfig?.placeholder,
      !hasValue && placeholderTextColor ? ({ color: placeholderTextColor } as TextStyle) : undefined,
      optionTextStyle,
    ]
    const iconStyle = [styles.icon, disabled && styles.iconDisabled, arrowStyle]

    const currentValue = Array.isArray(selected) ? selected.map((item) => item.value) : (selected as Option<T>)?.value

    return (
      <View style={outlineStyle}>
        <View testID="Select.Content" style={contentStyle}>
          {hasValue && renderValue ? (
            <View style={styles.value}>{renderValue(currentValue)}</View>
          ) : (
            <Text style={textStyle}>{hasValue ? (selectedValue as ReactNode) : placeholder}</Text>
          )}
          <Icon name={unfolded ? 'up' : 'down'} size={10} pointerEvents="none" style={iconStyle} />
        </View>
      </View>
    )
  }

  const renderSection = (item: Section<T>, index: number, last?: boolean) => {
    const { title, items: sectionOptions, style } = item
    const shownOptions = filterSelected
      ? (sectionOptions as Option<T>[])?.filter((opt) =>
          multiple
            ? !(selected as Option<T>[])?.find((item) => item.value === opt?.value)
            : opt?.value !== (selected as Option<T>)?.value,
        )
      : sectionOptions

    const allSelected = multiple && sectionHasAllSelected(item)
    const someSelected = multiple && sectionHasSomeSelected(item)

    return (
      <View key={`section-${title || index}`}>
        {title && (
          <SelectOption
            value={{ value: `section-${title}`, title }}
            checkbox={multiple}
            style={[styles.section, sectionStyle, style]}
            textStyle={styles.sectionTitle}
            disabled={!multiple}
            href={item?.href}
            onPress={selectSection(item)}
            selected={allSelected}
            checkboxStyle={someSelected && !allSelected ? styles.checkbox : undefined}
            testID="Select.Section"
          />
        )}
        {shownOptions.map((opt, optIndex) => renderOption(opt, last && optIndex === shownOptions.length - 1))}
      </View>
    )
  }
  const renderOption = (item: Option<T> | ReactElement, last?: boolean) => {
    if (isValidElement(item)) return item
    const option = item as Option<T>
    const isSelected = multiple && !!(selected as Option<T>[])?.find((opt) => opt.value === option?.value)

    return (
      <SelectOption
        key={`${option?.value}`}
        value={option}
        last={last}
        href={option?.href}
        onPress={select(option)}
        onFocus={updateOptionActive(true, option)}
        onBlur={updateOptionActive(false, option)}
        round={round}
        testID="Select.Option"
        style={[styles.option, optionStyle]} //, style]}
        textStyle={optionTextStyle}
        checkbox={multiple}
        selected={isSelected}
      />
    )
  }

  const renderOptions = () => {
    // const allOptions = !searchable
    //   ? items
    //   : (items as (Option<T> | Section<T> | ReactElement)[]).reduce((acc, item) => {
    //       const section = item as Section<T>
    //       if (isValidElement(item)) {
    //         acc.push(item)
    //       } else if (section?.items) {
    //         const filteredOptions = (section?.items as Option<T>[]).filter(
    //           (opt) => (opt.title?.toLowerCase().indexOf(search.toLowerCase()) || 0) >= 0
    //         )
    //         if (filteredOptions.length > 0) {
    //           acc.push({
    //             ...section,
    //             items: filteredOptions,
    //           })
    //         }
    //       } else if (((item as Option<T>).title?.toLowerCase().indexOf(search.toLowerCase()) || 0) >= 0) {
    //         acc.push(item)
    //       }
    //       return acc
    //     }, [] as (Option<T> | Section<T> | ReactElement)[])

    if (!items?.length) {
      if (typeof emptyPlaceholder === 'string')
        return <Text style={[styles.placeholder, styles.empty]}>{emptyPlaceholder}</Text>
      return emptyPlaceholder
    }

    const hasSections = !!(items as any[]).find((item: Section<T> | Option<T>) => {
      const section = item as Section<T>
      return !!section.items
    })

    if (hasSections) {
      return items.map((item, index) => {
        const sectionOptions = (item as Section<T>)?.items as Option<T>[]
        const shownOptions = filterSelected
          ? sectionOptions?.filter((opt) =>
              multiple
                ? !(selected as Option<T>[])?.find((item) => item.value === opt?.value)
                : opt?.value !== (selected as Option<T>)?.value,
            )
          : sectionOptions
        if (shownOptions?.length > 0) {
          const last = shownOptions && index === shownOptions.length - 1
          return renderSection(item as Section<T>, index, last)
        }
      })
    } else {
      const shownOptions = filterSelected
        ? (items as Option<T>[])?.filter((opt) =>
            multiple
              ? !(selected as Option<T>[])?.length ||
                !(selected as Option<T>[])?.find((item) => {
                  return item.value === opt?.value
                })
              : opt?.value !== (selected as Option<T>)?.value,
          )
        : items
      return shownOptions.map((opt, index) => renderOption(opt as Option<T>, index === shownOptions.length - 1))
    }
  }

  const renderButton = () => {
    return (
      <Pressable
        ref={button}
        disabled={disabled}
        onFocus={updateActive(true)}
        onBlur={updateActive(false)}
        onPress={toggleUnfolded}
        style={[
          Platform.select({
            // @ts-expect-error outline style none is not defined
            web: {
              outlineStyle: 'none',
            } as ViewStyle,
          }),
          styles.button,
        ]}
        testID="Select.Button"
        {...otherProps}
      >
        {renderContent}
      </Pressable>
    )
  }

  const renderSearch = () => {
    const inlineStyle = [{ minWidth: width }, unfolded && styles.unfolded, valueStyle]
    const style = [styles.search, unfolded && styles.unfolded]
    return (
      <TextInput
        ref={button}
        style={style}
        inlineStyle={inlineStyle}
        placeholder={placeholder}
        right={isSearching ? <ActivityIndicator color={Color.premiumBlack} /> : 'search'}
        onFocus={focus}
        onBlur={blur}
        value={unfolded ? search : getSearchValue()}
        onChangeText={(value) => {
          setSearch(value)
          onSearch?.(value)
        }}
        round={round}
        testID="Select.Search"
      />
    )
  }

  const optionsStyle = [styles.options, variantConfig?.options, contentContainerStyle, { maxHeight: optionsHeight }]
  const Wrapper = unfoldOnPressOutside ? OutsidePressHandler : View
  return (
    <Wrapper onOutsidePress={() => updateUnfolded(false)} style={[styles.container, style]} testID={testID}>
      {searchable ? renderSearch() : renderButton()}
      <View style={!unfolded && styles.wrapper}>
        {unfolded && (
          <ScrollView
            style={optionsStyle}
            onLayout={layout}
            onScroll={({ nativeEvent }) => {
              if (isCloseToBottom(nativeEvent)) {
                onEndReached?.()
              }
            }}
            scrollEventThrottle={400}
            testID="Select.Options"
          >
            {renderOptions()}
            {isFetching && <ActivityIndicator color={Color.premiumBlack} style={styles.fetching} />}
            {footer}
          </ScrollView>
        )}
      </View>
    </Wrapper>
  )
}

interface SkeletonProps extends ViewProps {
  contentStyle?: StyleProp<ViewStyle>
  round?: boolean
}

BaseSelect.Skeleton = (props: SkeletonProps) => {
  const { round, contentStyle, style, ...otherProps } = props

  const configStyle = round ? styleConfig.round : styleConfig.default

  const containerStyle = [styles.container, styles.select, styles.skeletonContainer, configStyle?.select, style]
  return (
    <View style={containerStyle} {...otherProps}>
      <Text.Skeleton contentStyle={contentStyle} />
    </View>
  )
}

export default BaseSelect
